import React, { useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { T, primaryBtn, inputStyle } from "../theme";
import { apiError } from "../api";
import { fontImport, responsiveCSS, LogoMark, CloseX, VersionBadge } from "./shared";

export default function Onboarding({ staticData, onFinish, overlay, onCancel, version, changelog }) {
  const nicheOptions = staticData.nicheOptions;
  const goalOptions = staticData.goalOptions;
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ nome: "", niche: nicheOptions[0], goal: goalOptions[0] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const steps = [
    { title: "Come si chiama il profilo da far crescere?",
      body: <input data-testid="onboarding-name-input" style={{ ...inputStyle, fontSize: 15, padding: "12px 14px" }} autoFocus placeholder="es. La mia attività"
        value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />,
      canNext: form.nome.trim().length > 0 },
    { title: "Che tipo di attività è?",
      body: (
        <div className="grid-2" style={{ maxHeight: 320, overflowY: "auto", paddingRight: 4 }}>
          {nicheOptions.map((n) => (
            <button data-testid={`onboarding-niche-${n.replace(/[^a-zA-Z]/g, "")}`} key={n} onClick={() => setForm({ ...form, niche: n })} style={{
              textAlign: "left", padding: "11px 12px", borderRadius: 8, cursor: "pointer", fontSize: 13, lineHeight: 1.3,
              background: form.niche === n ? T.surfaceAlt : "transparent",
              border: `1px solid ${form.niche === n ? T.amber : T.line}`, color: T.text, fontFamily: T.font,
            }}>{n}</button>
          ))}
        </div>
      ), canNext: true },
    { title: "Qual è il tuo obiettivo principale in questo momento?",
      body: (
        <div className="grid-2">
          {goalOptions.map((g) => (
            <button data-testid={`onboarding-goal-${g.replace(/[^a-zA-Z]/g, "")}`} key={g} onClick={() => setForm({ ...form, goal: g })} style={{
              textAlign: "left", padding: "12px 14px", borderRadius: 8, cursor: "pointer",
              background: form.goal === g ? T.surfaceAlt : "transparent",
              border: `1px solid ${form.goal === g ? T.amber : T.line}`, color: T.text, fontSize: 13, fontFamily: T.font, lineHeight: 1.3,
            }}>{g}</button>
          ))}
        </div>
      ), canNext: true },
  ];

  const s = steps[step];
  const isLast = step === steps.length - 1;

  async function finish() {
    setSaving(true);
    setError("");
    try { await onFinish(form); }
    catch (e) { setError(apiError(e)); }
    finally { setSaving(false); }
  }

  const content = (
    <div style={{ width: overlay ? 460 : 420, maxWidth: "88vw", position: "relative" }}>
      {overlay && onCancel && <CloseX onClick={onCancel} />}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 28, justifyContent: "center" }}>
        <LogoMark size={22} />
        <span style={{ fontFamily: T.fontHead, fontSize: 19 }}>So.Help</span>
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 24, justifyContent: "center" }}>
        {steps.map((_, i) => <div key={i} style={{ width: 34, height: 4, borderRadius: 2, background: i <= step ? T.amber : T.line }} />)}
      </div>
      <div style={{ fontFamily: T.fontHead, fontSize: 19, marginBottom: 16, textAlign: "center" }}>{s.title}</div>
      <div style={{ marginBottom: 26 }}>{s.body}</div>
      {error && (
        <div data-testid="onboarding-error" style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: "#FDECEA", border: "1px solid #F5C6C0", color: "#B3261E", fontSize: 13, textAlign: "center" }}>
          {error}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
        {step > 0 && (
          <button data-testid="onboarding-back-btn" onClick={() => setStep(step - 1)} style={{
            display: "flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${T.line}`,
            color: T.textDim, borderRadius: 7, padding: "9px 14px", fontSize: 13, cursor: "pointer",
          }}><ArrowLeft size={14} /> Indietro</button>
        )}
        <button data-testid="onboarding-next-btn" disabled={!s.canNext || saving} onClick={() => (isLast ? finish() : setStep(step + 1))}
          style={{ ...primaryBtn, opacity: s.canNext && !saving ? 1 : 0.5, display: "flex", alignItems: "center", gap: 6 }}>
          {saving ? "Un momento…" : isLast ? "Crea il mio piano" : "Continua"} {!saving && <ArrowRight size={14} />}
        </button>
      </div>
    </div>
  );

  if (overlay) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(30,30,51,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, fontFamily: T.font, color: T.text }}>
        <div className="modal-card" style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 14, padding: 32, boxShadow: "0 20px 50px rgba(30,30,60,0.3)" }}>
          {content}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: T.font, color: T.text }}>
      <style>{`${fontImport} ${responsiveCSS}`}</style>
      <VersionBadge version={version} changelog={changelog} />
      {content}
    </div>
  );
}
